import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Table, Tag, Typography, Space, Button, Alert, Skeleton } from 'antd';
import {
  ShoppingCartOutlined,
  ShopOutlined,
  InboxOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  BarChartOutlined,
  PieChartOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useDashboard } from '../hooks/useDashboard';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../hooks/useResponsive';
import moment from 'moment';
import { Line, Column, Pie } from '@ant-design/plots';
import { dashboardService } from '../services/dashboardService';
import { API_BASE_URL } from '../services/apiService';
import { RBACService, PERMISSIONS } from '../utils/rbac';
import './Dashboard.css';

const { Title } = Typography;

const Dashboard: React.FC = () => {
  const { isMobile } = useResponsive();
  const chartHeight = isMobile ? 240 : 300;
  const { 
    stats, 
    recentTransactions, 
    loading, 
    error, 
    refreshData, 
    lastUpdated 
  } = useDashboard(true, 30000); // Auto-refresh every 30 seconds
  
  const navigate = useNavigate();
  const currentUser = RBACService.getCurrentUser();

  // Permission flags for cards & charts (users see only what they're allowed)
  const canViewSales = RBACService.hasPermission(PERMISSIONS.POS_MANAGE);
  const canViewInventory = RBACService.hasAnyPermission([
    PERMISSIONS.INVENTORY_READ,
    PERMISSIONS.INVENTORY_CREATE,
    PERMISSIONS.INVENTORY_UPDATE,
    PERMISSIONS.INVENTORY_DELETE,
    PERMISSIONS.STOCK_MOVEMENT_CREATE,
    PERMISSIONS.STOCK_ADJUSTMENT_CREATE,
    PERMISSIONS.STOCK_ADJUSTMENT_APPROVE,
    PERMISSIONS.STOCK_TRANSFER_CREATE,
    PERMISSIONS.STOCK_TRANSFER_RECEIVE,
  ]);
  const canViewProducts = RBACService.hasAnyPermission([PERMISSIONS.PRODUCT_READ, PERMISSIONS.PRODUCT_CREATE]);
  const canViewPO = RBACService.hasPermission(PERMISSIONS.PO_READ);
  const [salesTrend, setSalesTrend] = useState<Array<{ date: string; value: number }>>([]);
  const [paymentSplit, setPaymentSplit] = useState<Array<{ type: string; value: number }>>([]);
  const [topProducts, setTopProducts] = useState<Array<{ name: string; value: number }>>([]);
  const [stockHistogram, setStockHistogram] = useState<Array<{ bin: string; count: number }>>([]);
  const [chartsLoading, setChartsLoading] = useState(false);

  // Load charts after stat cards finish (avoids competing requests right after login)
  const chartDays = 10;
  useEffect(() => {
    if (loading) return;
    if (!canViewSales && !canViewInventory) return;

    let cancelled = false;
    const loadCharts = async () => {
      setChartsLoading(true);
      try {
        const start = moment().subtract(chartDays - 1, 'day').format('YYYY-MM-DD');
        const end = moment().format('YYYY-MM-DD');
        const isLocal = /localhost|127\.0\.0\.1/.test(API_BASE_URL);
        const txLimit = isLocal ? 280 : 160;
        const stockLimit = isLocal ? 180 : 100;
        const [txns, stock] = await Promise.all([
          canViewSales
            ? dashboardService.getPOSTransactions({ start_date: start, end_date: end, limit: txLimit })
            : Promise.resolve([]),
          canViewInventory
            ? dashboardService.getStockDetailed({ limit: stockLimit })
            : Promise.resolve({ items: [] }),
        ]);
        if (cancelled) return;

        if (canViewSales) {
          // Sales over time (daily totals)
          const byDay: Record<string, number> = {};
          const cur = moment(start);
          const endM = moment(end);
          while (cur.isSameOrBefore(endM, 'day')) {
            byDay[cur.format('YYYY-MM-DD')] = 0;
            cur.add(1, 'day');
          }
          for (const t of txns || []) {
            const d = String(t.sale_date || '').slice(0, 10);
            byDay[d] = (byDay[d] || 0) + Number(t.total_amount || 0);
          }
          setSalesTrend(Object.entries(byDay).map(([date, value]) => ({ date, value: +Number(value).toFixed(2) })));

          // Payment split
          const pay: Record<string, number> = {};
          for (const t of txns || []) {
            const k = String(t.payment_method || 'CASH');
            pay[k] = (pay[k] || 0) + Number(t.total_amount || 0);
          }
          const label: Record<string, string> = { CASH: 'Cash', CARD: 'Card', BANK_TRANSFER: 'Bank Transfer' };
          setPaymentSplit(Object.entries(pay).map(([k, v]) => ({ type: label[k] || k, value: +Number(v).toFixed(2) })));

          // Top products by quantity
          const prod: Record<string, number> = {};
          for (const t of txns || []) {
            for (const it of (t.transaction_items || [])) {
              const name = String(it.product_name || it.product_code || it.product_id || 'Unknown');
              prod[name] = (prod[name] || 0) + Number(it.quantity || 0);
            }
          }
          setTopProducts(
            Object.entries(prod)
              .map(([name, value]) => ({ name, value }))
              .sort((a, b) => b.value - a.value)
              .slice(0, 10)
          );
        } else {
          setSalesTrend([]);
          setPaymentSplit([]);
          setTopProducts([]);
        }

        // Stock histogram (distribution of available stock units)
        if (canViewInventory) {
          const items = stock?.items || [];
          const bins: Array<{ label: string; test: (n: number) => boolean }> = [
            { label: '0', test: (n) => n <= 0 },
            { label: '1–5', test: (n) => n >= 1 && n <= 5 },
            { label: '6–10', test: (n) => n >= 6 && n <= 10 },
            { label: '11–20', test: (n) => n >= 11 && n <= 20 },
            { label: '21–50', test: (n) => n >= 21 && n <= 50 },
            { label: '51+', test: (n) => n >= 51 },
          ];
          const counts: Record<string, number> = Object.fromEntries(bins.map((b) => [b.label, 0]));
          for (const s of items) {
            const n = Number(s?.quantities?.available ?? 0);
            const bin = bins.find((b) => b.test(n))?.label || '0';
            counts[bin] = (counts[bin] || 0) + 1;
          }
          setStockHistogram(bins.map((b) => ({ bin: b.label, count: counts[b.label] || 0 })));
        } else {
          setStockHistogram([]);
        }
      } catch (e) {
        if (!cancelled) {
          setSalesTrend([]);
          setPaymentSplit([]);
          setTopProducts([]);
          setStockHistogram([]);
        }
      } finally {
        if (!cancelled) setChartsLoading(false);
      }
    };
    loadCharts();
    return () => {
      cancelled = true;
    };
  }, [loading, canViewSales, canViewInventory]);

  const handleCardClick = (path: string) => {
    navigate(path);
  };

  const transactionColumns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={type === 'sale' ? 'green' : 'blue'}>
          {type.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `Nu. ${amount.toLocaleString()}`,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'completed' ? 'green' : 
                     status === 'pending' ? 'orange' : 'red';
        return <Tag color={color}>{status.toUpperCase()}</Tag>;
      },
    },
  ];

  return (
    <div className="dashboard-page">
      {/* Welcome header */}
      <div className="dashboard-header">
        <div>
          <Title level={3} style={{ margin: 0, fontWeight: 600, color: '#1e293b' }}>
            Welcome back{currentUser?.name ? `, ${currentUser.name.split(' ')[0]}` : ''}
          </Title>
          <span style={{ color: '#64748b', fontSize: 14, marginTop: 4, display: 'block' }}>
            Here’s an overview of your business today
          </span>
        </div>
        {lastUpdated && (
          <span style={{ color: '#94a3b8', fontSize: 13 }}>
            Updated {lastUpdated.toLocaleTimeString()}
          </span>
        )}
      </div>
      
      {error && (
        <Alert
          message="Error Loading Dashboard"
          description={error}
          type="error"
          action={
            <Button size="small" onClick={refreshData}>
              Retry
            </Button>
          }
          style={{ marginBottom: 16 }}
          closable
        />
      )}
      
      {/* Stats Cards – skeleton while loading so layout appears immediately */}
      <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
        {loading ? (
          <>
            {[1, 2, 3, 4].map((i) => (
              <Col xs={24} sm={12} lg={6} key={i}>
                <Card><Skeleton active paragraph={{ rows: 2 }} /></Card>
              </Col>
            ))}
          </>
        ) : (
          <>
        {canViewSales && (
          <Col xs={24} sm={12} lg={6}>
            <Card className="stats-card dashboard-stat-card dashboard-stat-card--blue" hoverable onClick={() => handleCardClick('/sales')}>
              <div className="dashboard-stat-card__inner">
                <div className="dashboard-stat-card__top">
                  <div className="dashboard-stat-card__label">Today Transactions</div>
                  <div className="dashboard-stat-card__icon-badge dashboard-stat-card__icon-badge--blue">
                    <ShoppingCartOutlined className="dashboard-stat-card__icon" />
                  </div>
                </div>
                <div className="dashboard-stat-card__value">{stats.totalSales}</div>
                <div className="dashboard-stat-card__sub">
                  <ArrowUpOutlined style={{ color: stats.salesGrowth >= 0 ? '#059669' : '#dc2626', fontSize: '12px' }} />
                  <span>{stats.salesGrowth}%</span>
                </div>
              </div>
            </Card>
          </Col>
        )}
        {canViewInventory && (
          <Col xs={24} sm={12} lg={6}>
            <Card className="stats-card dashboard-stat-card dashboard-stat-card--amber" hoverable onClick={() => handleCardClick('/inventory')}>
              <div className="dashboard-stat-card__inner">
                <div className="dashboard-stat-card__top">
                  <div className="dashboard-stat-card__label">Low Stock Items</div>
                  <div className="dashboard-stat-card__icon-badge dashboard-stat-card__icon-badge--amber">
                    <ShopOutlined className="dashboard-stat-card__icon" />
                  </div>
                </div>
                <div className="dashboard-stat-card__value">{stats.totalPurchaseOrders}</div>
              </div>
            </Card>
          </Col>
        )}
        {canViewInventory && (
          <Col xs={24} sm={12} lg={6}>
            <Card className="stats-card dashboard-stat-card dashboard-stat-card--indigo" hoverable onClick={() => handleCardClick('/products')}>
              <div className="dashboard-stat-card__inner">
                <div className="dashboard-stat-card__top">
                  <div className="dashboard-stat-card__label">Stock Products</div>
                  <div className="dashboard-stat-card__icon-badge dashboard-stat-card__icon-badge--indigo">
                    <InboxOutlined className="dashboard-stat-card__icon" />
                  </div>
                </div>
                <div className="dashboard-stat-card__value">{stats.totalProducts}</div>
              </div>
            </Card>
          </Col>
        )}
        {canViewSales && (
          <Col xs={24} sm={12} lg={6}>
            <Card className="stats-card dashboard-stat-card dashboard-stat-card--emerald" hoverable onClick={() => handleCardClick('/reports')}>
              <div className="dashboard-stat-card__inner">
                <div className="dashboard-stat-card__top">
                  <div className="dashboard-stat-card__label">Today Revenue</div>
                  <div className="dashboard-stat-card__icon-badge dashboard-stat-card__icon-badge--emerald">
                    <BarChartOutlined className="dashboard-stat-card__icon" />
                  </div>
                </div>
                <div className="dashboard-stat-card__value">Nu. {stats.totalRevenue.toLocaleString()}</div>
                <div className="dashboard-stat-card__sub">
                  {stats.revenueGrowth >= 0 ? (
                    <ArrowUpOutlined style={{ color: '#059669', fontSize: '12px' }} />
                  ) : (
                    <ArrowDownOutlined style={{ color: '#dc2626', fontSize: '12px' }} />
                  )}
                  <span>{Math.abs(stats.revenueGrowth)}%</span>
                </div>
              </div>
            </Card>
          </Col>
        )}
        {canViewPO && (
          <Col xs={24} sm={12} lg={6}>
            <Card className="stats-card dashboard-stat-card dashboard-stat-card--violet" hoverable onClick={() => handleCardClick('/purchase-orders')}>
              <div className="dashboard-stat-card__inner">
                <div className="dashboard-stat-card__top">
                  <div className="dashboard-stat-card__label">Purchase Orders</div>
                  <div className="dashboard-stat-card__icon-badge dashboard-stat-card__icon-badge--violet">
                    <FileTextOutlined className="dashboard-stat-card__icon" />
                  </div>
                </div>
                <div className="dashboard-stat-card__value">{stats.poCount ?? 0}</div>
              </div>
            </Card>
          </Col>
        )}
          </>
        )}
      </Row>

      {/* Overview Charts – shown based on permissions */}
      {(canViewSales || canViewInventory) && (
        <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
          {canViewSales && (
            <>
              <Col xs={24} lg={12}>
                <Card
                  className="page-table-card page-table-card--blue"
                  title={<span className="dashboard-chart-title"><BarChartOutlined /> Sales Trend (10 days)</span>}
                  style={{ borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
                  bodyStyle={{ padding: 12 }}
                  loading={loading || chartsLoading}
                >
                  <Line
                    data={salesTrend}
                    xField="date"
                    yField="value"
                    height={chartHeight}
                    smooth
                    xAxis={{ tickCount: isMobile ? 4 : 7 }}
                    yAxis={{ label: { formatter: (v: any) => `Nu. ${Number(v).toLocaleString()}` } }}
                    tooltip={{ formatter: (d: any) => ({ name: 'Revenue', value: `Nu. ${Number(d.value).toFixed(2)}` }) }}
                    point={{ size: 3 }}
                  />
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card
                  className="page-table-card page-table-card--indigo"
                  title={<span className="dashboard-chart-title"><PieChartOutlined /> Payment Method Split</span>}
                  style={{ borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
                  bodyStyle={{ padding: 12 }}
                  loading={loading || chartsLoading}
                >
                  <Pie
                    data={paymentSplit}
                    angleField="value"
                    colorField="type"
                    radius={0.9}
                    height={chartHeight}
                    legend={{ position: isMobile ? 'bottom' : 'right' }}
                    label={false}
                  />
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card
                  className="page-table-card page-table-card--green"
                  title={<span className="dashboard-chart-title"><BarChartOutlined /> Top Products (Qty)</span>}
                  style={{ borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
                  bodyStyle={{ padding: 12 }}
                  loading={loading || chartsLoading}
                >
                  <Column
                    data={topProducts}
                    xField="name"
                    yField="value"
                    height={chartHeight}
                    xAxis={{ label: { autoHide: true, autoRotate: true } }}
                    yAxis={{ label: { formatter: (v: any) => String(Number(v).toLocaleString()) } }}
                    tooltip={{ formatter: (d: any) => ({ name: 'Qty', value: String(d.value) }) }}
                    columnStyle={{ radius: [6, 6, 0, 0] }}
                  />
                </Card>
              </Col>
            </>
          )}
          {canViewInventory && (
            <Col xs={24} sm={12} lg={12}>
              <Card
                className="page-table-card page-table-card--teal"
                title={<span className="dashboard-chart-title"><BarChartOutlined /> Stock Level Histogram</span>}
                style={{ borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
                bodyStyle={{ padding: 12 }}
                loading={loading || chartsLoading}
              >
                <Column
                  data={stockHistogram}
                  xField="bin"
                  yField="count"
                  height={chartHeight}
                  xAxis={{ title: { text: 'Available units' } }}
                  yAxis={{ title: { text: 'Products' } }}
                  tooltip={{ formatter: (d: any) => ({ name: 'Products', value: String(d.count) }) }}
                  columnStyle={{ radius: [6, 6, 0, 0] }}
                />
              </Card>
            </Col>
          )}
        </Row>
      )}

      {/* Recent Transactions – only for users with sales permission */}
      {canViewSales && (
        <Card 
          className="page-table-card page-table-card--emerald"
          title={<span className="dashboard-chart-title">Recent Transactions</span>}
          style={{ 
            marginBottom: 28,
            borderRadius: 12,
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(15, 23, 42, 0.10)'
          }}
        >
          <Table
            columns={transactionColumns}
            dataSource={recentTransactions}
            rowKey="id"
            pagination={{ pageSize: isMobile ? 5 : 10 }}
            loading={loading}
            style={{ borderRadius: 8 }}
            size={isMobile ? 'small' : 'middle'}
            scroll={isMobile ? { x: 400 } : undefined}
          />
        </Card>
      )}

      {/* Quick Actions – permission-aware */}
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card title="Quick Actions" size="small" className="page-table-card page-table-card--violet">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {canViewSales && <a href="/sales" onClick={(e) => { e.preventDefault(); navigate('/sales'); }} style={{ cursor: 'pointer' }}>Open Sales Reports</a>}
              {canViewPO && RBACService.hasPermission(PERMISSIONS.PO_CREATE) && <a href="/purchase-orders" onClick={(e) => { e.preventDefault(); navigate('/purchase-orders'); }} style={{ cursor: 'pointer' }}>Create Purchase Order</a>}
              {canViewProducts && RBACService.hasPermission(PERMISSIONS.PRODUCT_CREATE) && <a href="/products" onClick={(e) => { e.preventDefault(); navigate('/products'); }} style={{ cursor: 'pointer' }}>Add New Product</a>}
              {canViewInventory && RBACService.hasAnyPermission([PERMISSIONS.STOCK_ADJUSTMENT_CREATE, PERMISSIONS.INVENTORY_UPDATE]) && <a href="/inventory" onClick={(e) => { e.preventDefault(); navigate('/inventory'); }} style={{ cursor: 'pointer' }}>Stock Adjustment</a>}
              {!canViewSales && !canViewPO && !canViewProducts && !canViewInventory && (
                <span style={{ color: '#94a3b8' }}>Use the sidebar for quick access</span>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
